// Utility functions for handling external authentication tokens

/**
 * Extract tokens from URL parameters (common OAuth2 pattern)
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} Object containing idToken and accessToken if found
 */
export const extractTokensFromUrl = (searchParams) => {
  const idToken = searchParams.get('id_token') || searchParams.get('token');
  const accessToken = searchParams.get('access_token') || searchParams.get('access_token');
  
  return {
    idToken,
    accessToken
  };
};

/**
 * Extract tokens from headers (for scenarios where auth service passes tokens via headers)
 * This would typically be used in an API response or server-side scenario
 * @param {Headers} headers - Request headers
 * @returns {Object} Object containing idToken and accessToken if found
 */
export const extractTokensFromHeaders = (headers) => {
  const idToken = headers.get('X-ID-Token') || headers.get('Authorization-ID');
  const accessToken = headers.get('X-Access-Token') || headers.get('Authorization');
  
  return {
    idToken: idToken ? idToken.replace('Bearer ', '') : null,
    accessToken: accessToken ? accessToken.replace('Bearer ', '') : null
  };
};

/**
 * Extract tokens from fragment (hash) in URL - common with implicit flow
 * @param {string} hash - window.location.hash
 * @returns {Object} Object containing idToken and accessToken if found
 */
export const extractTokensFromFragment = (hash) => {
  if (!hash || !hash.startsWith('#')) {
    return { idToken: null, accessToken: null };
  }
  
  const params = new URLSearchParams(hash.substring(1));
  const idToken = params.get('id_token');
  const accessToken = params.get('access_token');
  
  return {
    idToken,
    accessToken
  };
};

/**
 * Check if current URL contains authentication callback parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {boolean} True if auth callback parameters are present
 */
export const isAuthCallback = (searchParams) => {
  return !!(searchParams.get('code') || searchParams.get('id_token') || searchParams.get('access_token'));
};

/**
 * Clean auth parameters from URL after processing
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {string} Clean URL without auth parameters
 */
export const cleanAuthParamsFromUrl = (searchParams) => {
  const paramsToRemove = ['code', 'id_token', 'access_token', 'token', 'state', 'session_state'];
  
  paramsToRemove.forEach(param => searchParams.delete(param));
  
  const cleanUrl = window.location.pathname + (searchParams.toString() ? '?' + searchParams.toString() : '');
  return cleanUrl;
};